import React, { useRef, useState } from "react";
import { Search, Package } from "lucide-react";
import reqProduto from "../../../services/reqMultiTenant/reqProduto";
import libFloat from "../../../util/libFloat";
import Notification from "../../toast";

const PER_PAGE = 15; // mesmo padrão de paginação de product/context/index.jsx

// Passo 1 do seletor de produto: busca + lista de resultados (sem catálogo/fotos grandes,
// já que esse uso é interno/operacional, não uma vitrine pro cliente final).
export default function ProductSearchList({ onSelectProduct }) {
	const [termo, setTermo] = useState("");
	const [produtos, setProdutos] = useState([]);
	const [loading, setLoading] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [buscou, setBuscou] = useState(false);
	const [totalPage, setTotalPage] = useState(0); // >0 quando já chegou na última página
	const currentPage = useRef(0);
	const inputRef = useRef(null);
	const itemRefs = useRef([]);
	const [activeIndex, setActiveIndex] = useState(-1); // índice com foco/destaque na lista, via teclado

	const fetchProdutos = async (paramValue, page) => {
		// active "SIM" (string, não boolean) e tipoproduto "pai" seguem o mesmo padrão de
		// product/context/index.jsx. paramValue vazio é válido — traz tudo, sem filtro.
		const response = await reqProduto.listPaginate("nome", paramValue, "SIM", page, "pai", PER_PAGE);
		const result = response.data || [];
		return result;
	};

	// Busca do zero (botão "Buscar" ou Enter) — com ou sem termo digitado, sempre lista algo
	const handleBuscar = async () => {
		setLoading(true);
		try {
			const data = await fetchProdutos(termo.trim(), 1);
			setProdutos(data);
			setBuscou(true);
			setActiveIndex(-1);

			if (data.length < PER_PAGE) {
				setTotalPage(1);
			} else {
				setTotalPage(0);
				currentPage.current = 1;
			}
		} catch (error) {
			Notification.showToast({ message: "Erro ao buscar produtos.", type: "error" });
			setProdutos([]);
		} finally {
			setLoading(false);
		}
	};

	// Carrega mais resultados da mesma busca, acumulando na lista
	const handleCarregarMais = async () => {
		if (totalPage > 0) return;

		setLoadingMore(true);
		try {
			const nextPage = currentPage.current + 1;
			const data = await fetchProdutos(termo.trim(), nextPage);

			if (data.length > 0) {
				setProdutos((prev) => [...prev, ...data]);
			}

			if (data.length === 0 || data.length < PER_PAGE) {
				setTotalPage(nextPage);
			} else {
				currentPage.current = nextPage;
			}
		} catch (error) {
			Notification.showToast({ message: "Erro ao carregar mais produtos.", type: "error" });
		} finally {
			setLoadingMore(false);
		}
	};

	// Move o foco (DOM) pro item da lista — o destaque visual acompanha o foco via onFocus,
	// então navegar com o teclado já deixa claro em qual linha o usuário está.
	const focusItem = (index) => {
		itemRefs.current[index]?.focus();
	};

	const handleInputKeyDown = (e) => {
		if (e.key === "Enter") {
			e.preventDefault();
			handleBuscar();
			return;
		}
		if (e.key === "ArrowDown" && produtos.length > 0) {
			e.preventDefault();
			focusItem(0);
		}
	};

	// Seta para cima/baixo navega entre os produtos; no primeiro item, seta para cima volta
	// pro campo de busca. Enter/Espaço já seleciona nativamente (é um <button>).
	const handleItemKeyDown = (e, index) => {
		if (e.key === "ArrowDown") {
			e.preventDefault();
			if (index < produtos.length - 1) focusItem(index + 1);
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			if (index === 0) {
				inputRef.current?.focus();
				setActiveIndex(-1);
			} else {
				focusItem(index - 1);
			}
		}
	};

	return (
		<div className="flex h-full flex-col">
			{/* Busca */}
			<div className="mb-4 flex flex-shrink-0 gap-2">
				<div className="relative flex-1">
					<Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
					<input
						ref={inputRef}
						type="text"
						value={termo}
						onChange={(e) => setTermo(e.target.value)}
						onKeyDown={handleInputKeyDown}
						placeholder="Buscar produto por nome (ou deixe em branco pra listar todos)..."
						autoFocus
						className="h-11 w-full rounded-xl border border-gray-200 bg-gray-50 pl-9 pr-3 text-sm text-gray-700 transition-all focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/10"
					/>
				</div>
				<button
					type="button"
					onClick={handleBuscar}
					className="h-11 flex-shrink-0 rounded-xl bg-blue-600 px-5 text-xs font-bold text-white transition-all hover:bg-blue-700 active:scale-95"
				>
					Buscar
				</button>
			</div>

			{/* Resultados */}
			<div className="min-h-0 flex-1 overflow-y-auto">
				{loading && <div className="p-6 text-center text-sm text-gray-500">Buscando produtos...</div>}

				{!loading && buscou && produtos.length === 0 && (
					<div className="p-6 text-center text-sm text-gray-500">Nenhum produto encontrado.</div>
				)}

				{!loading && !buscou && (
					<div className="p-6 text-center text-sm text-gray-400">
						Digite o nome do produto e busque, ou clique em "Buscar" pra listar todos.
					</div>
				)}

				<ul className="space-y-2 py-1">
					{produtos.map((produto, index) => {
						const isActive = activeIndex === index;
						return (
							<li key={produto.id_produto}>
								<button
									ref={(el) => (itemRefs.current[index] = el)}
									type="button"
									onClick={() => onSelectProduct(produto)}
									onFocus={() => setActiveIndex(index)}
									onKeyDown={(e) => handleItemKeyDown(e, index)}
									className={`flex w-full items-center gap-3.5 rounded-[18px] border p-3.5 text-left transition-all ${
										isActive
											? "border-blue-500 bg-blue-50/50 shadow-md ring-2 ring-blue-500/20"
											: "border-gray-100 bg-white hover:border-blue-200 hover:bg-blue-50/30 hover:shadow-sm"
									}`}
								>
									<div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl bg-blue-600/10 text-blue-600">
										<Package size={20} />
									</div>
									<div className="min-w-0 flex-1">
										<p className="truncate text-sm font-black text-gray-800 tracking-tight">
											{produto.nome}
										</p>
										<div className="mt-0.5 flex items-center gap-2 text-xs font-semibold text-gray-500">
											{produto.und && (
												<span className="rounded-md bg-gray-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-gray-600">
													{produto.und}
												</span>
											)}
											{produto.qtatual !== undefined && produto.qtatual !== null && (
												<span>
													Estoque:{" "}
													<strong className="text-gray-700">{produto.qtatual}</strong>
												</span>
											)}
										</div>
									</div>
									<div className="flex-shrink-0 text-right">
										{produto.valorpromoapi > 0 && produto.valorpromoapi < produto.valor ? (
											<div className="flex flex-col items-end">
												<span className="text-[10px] text-gray-400 line-through">
													{libFloat.strToCurrency(produto.valor)}
												</span>
												<span className="rounded-xl bg-emerald-50 px-2 py-1 text-sm font-black text-emerald-600 border border-emerald-100">
													{libFloat.strToCurrency(produto.valorpromoapi)}
												</span>
											</div>
										) : (
											<span className="rounded-xl bg-blue-50 px-3 py-1.5 text-sm font-black text-blue-600 border border-blue-100">
												{libFloat.strToCurrency(produto.valor || 0)}
											</span>
										)}
									</div>
								</button>
							</li>
						);
					})}
				</ul>

				{!loading && buscou && produtos.length > 0 && totalPage === 0 && (
					<div className="p-3 text-center">
						<button
							type="button"
							onClick={handleCarregarMais}
							disabled={loadingMore}
							className="rounded-xl border border-gray-200 px-4 py-2 text-xs font-bold text-gray-600 hover:bg-gray-50 disabled:opacity-50"
						>
							{loadingMore ? "Carregando..." : "Carregar mais"}
						</button>
					</div>
				)}
			</div>
		</div>
	);
}
